<script setup lang="ts">
  import { IconEdit } from '@gat-ship/icons'
  import { testIds } from '@gat-ship/types'
  import { useCommandId } from '@gat-ship/utils'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    InputGroupTextarea,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import {
    useAccessRoleDetailProvider,
    type ShowChangeAccessRoleNameDialogParams
  } from '../providers/access-role-detail-context'

  const accessRoleProvider = useAccessRoleDetailProvider()
  const isOpen = defineModel<boolean>('open', { default: false })
  const { newCommandId } = useCommandId()

  const changeAccessRoleNameSchema = z.object({
    roleId: z.string().min(1),
    name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
    description: z
      .string()
      .min(1, 'Description is required')
      .max(500, 'Max 500 characters')
  })
  type ChangeAccessRoleNameFormValues = z.infer<
    typeof changeAccessRoleNameSchema
  >

  const form = useForm({
    defaultValues: {
      roleId: '',
      name: '',
      description: ''
    } as ChangeAccessRoleNameFormValues,
    validators: {
      onChange: changeAccessRoleNameSchema
    },
    onSubmit: async ({ value }) => {
      await accessRoleProvider.dataSource.changeAccessRoleName(
        value.roleId,
        value.name,
        value.description
      )
      isOpen.value = false
    }
  })

  function showModal({ accessRole }: ShowChangeAccessRoleNameDialogParams) {
    newCommandId()
    form.reset({
      roleId: accessRole.id,
      name: accessRole.name,
      description: accessRole.description
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEdit class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Name
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="name" label="Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.company.modal.nameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="description"
            label="Description"
            required
          >
            <template #default="{ field }">
              <InputGroupTextarea
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.company.modal.descriptionInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
